import type { Element } from './elements.ts';
import { SPECIES } from './species.ts';

export interface TraitDef {
  id: string;
  name: string;
  rare: boolean;
  desc: string;
  /** Effects the simulation reads. */
  dmgElement?: { element: Element; mult: number };
  dmgMult?: number;
  hpMult?: number;
  dropMult?: number;
  catchMult?: number;
  xpMult?: number;
  coinMult?: number;
  speedMult?: number;
}

const el = (element: Element, name: string, mult = 1.1): TraitDef => ({
  id: `${element}-touched`, name, rare: false, desc: `+${Math.round((mult - 1) * 100)}% ${element} damage`, dmgElement: { element, mult },
});

export const TRAITS: TraitDef[] = [
  el('flame', 'Kindled'), el('tide', 'Tidal'), el('grove', 'Rooted'), el('storm', 'Charged'),
  el('stone', 'Granite'), el('frost', 'Rimebound'), el('shade', 'Shadowed'), el('light', 'Radiant'),
  { id: 'sturdy', name: 'Sturdy', rare: false, desc: '+8% HP', hpMult: 1.08 },
  { id: 'fierce', name: 'Fierce', rare: false, desc: '+5% all damage', dmgMult: 1.05 },
  { id: 'nimble', name: 'Nimble', rare: false, desc: '+6% attack speed', speedMult: 1.06 },
  { id: 'scavenger', name: 'Scavenger', rare: false, desc: '+5% drop chance in hunts', dropMult: 1.05 },
  { id: 'gentle', name: 'Gentle Hand', rare: false, desc: '+5% catch chance', catchMult: 1.05 },
  { id: 'studious', name: 'Studious', rare: false, desc: '+5% trainer and creature XP from kills', xpMult: 1.05 },
  { id: 'thrifty', name: 'Thrifty', rare: false, desc: '+5% coins from kills', coinMult: 1.05 },
  { id: 'hardy', name: 'Hardy', rare: false, desc: '+4% HP, +2% damage', hpMult: 1.04, dmgMult: 1.02 },
  // Rare traits: 1 in 500 catches.
  { id: 'mythborn', name: 'Mythborn', rare: true, desc: '+15% all damage', dmgMult: 1.15 },
  { id: 'colossus', name: 'Colossus', rare: true, desc: '+20% HP', hpMult: 1.2 },
  { id: 'windstep', name: 'Windstep', rare: true, desc: '+15% attack speed', speedMult: 1.15 },
  { id: 'treasure', name: 'Treasure Sense', rare: true, desc: '+15% drop chance in hunts', dropMult: 1.15 },
  { id: 'binder', name: 'Soulbinder', rare: true, desc: '+15% catch chance', catchMult: 1.15 },
  { id: 'sage', name: 'Sage', rare: true, desc: '+15% trainer and creature XP from kills', xpMult: 1.15 },
  { id: 'midas', name: 'Midas', rare: true, desc: '+15% coins from kills', coinMult: 1.15 },
  { id: 'paragon', name: 'Paragon', rare: true, desc: '+8% damage, +8% HP, +4% speed', dmgMult: 1.08, hpMult: 1.08, speedMult: 1.04 },
];

export const TRAIT_BY_ID: Record<string, TraitDef> = Object.fromEntries(TRAITS.map((t) => [t.id, t]));

/**
 * An element trait always counts for the creature's own element (2026-10-20), so no trait is a dead roll: one whose
 * element the creature lacks acts and shows as the element trait of its first element (a Grove creature's Radiant is
 * Rooted, +10% grove damage). Any other trait comes back unchanged. The roll draws nothing extra (rollTrait still picks
 * from every trait), so a seed rolls what it always did. New creatures store the mapped id; older ones keep the id they
 * rolled, and everything that reads a trait for its effect or its name maps it here.
 */
export function ownTrait(traitId: string, elements: readonly Element[]): string {
  const el = TRAIT_BY_ID[traitId]?.dmgElement?.element;
  if (!el || !elements.length || elements.includes(el)) return traitId;
  const own = `${elements[0]}-touched`;
  return TRAIT_BY_ID[own] ? own : traitId;
}

/** ownTrait for a species by id (unknown species: unchanged). */
export const speciesTrait = (traitId: string, speciesId: string): string => ownTrait(traitId, SPECIES[speciesId]?.elements ?? []);

/** A creature's trait as it acts and shows (speciesTrait on its stored id). */
export const traitOf = (c: { species: string; rolls: { trait: string } }): TraitDef | undefined => TRAIT_BY_ID[speciesTrait(c.rolls.trait, c.species)];
